import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  Instagram,
  Calendar,
  MessageSquare,
  ChevronRight,
  Clock,
  DollarSign,
  Star,
  Share,
  ExternalLink
} from "lucide-react";
import { QRCodeSVG } from 'qrcode.react';

const StylistPublicProfile = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    fetchStylistProfile();
  }, [providerId]);

  const fetchStylistProfile = async () => {
    try {
      const response = await axios.get(`${API}/stylist/${providerId}`);
      setProfile(response.data.profile);
      setServices(response.data.services || []);
      setPortfolio(response.data.portfolio || []);
    } catch (error) {
      console.error("Error fetching stylist:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSharePreferences = () => {
    navigate(`/checkin/${providerId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <h1 className="text-xl font-bold mb-2">Stylist not found</h1>
        <p className="text-gray-600 text-sm">This profile may have been moved or deleted.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-lg mx-auto px-4 py-8 text-center">
          {/* Avatar */}
          <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4 text-3xl font-bold">
            {profile.business_name?.[0] || "S"}
          </div>
          
          <h1 className="text-2xl font-bold">{profile.business_name}</h1>
          <p className="text-gray-300 capitalize">{profile.service_type} Stylist</p>
          
          {profile.instagram && (
            <a 
              href={`https://instagram.com/${profile.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-gray-500 hover:text-white mt-2"
            >
              <Instagram className="w-4 h-4" />
              @{profile.instagram}
            </a>
          )}
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
            <div>
              <p className="text-2xl font-bold">{services.length}</p>
              <p className="text-xs text-gray-500">Services</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{portfolio.length}</p>
              <p className="text-xs text-gray-500">Portfolio</p>
            </div>
            <div>
              <p className="text-2xl font-bold">4.9</p>
              <p className="text-xs text-gray-500">Rating</p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Bio */}
        {profile.bio && (
          <p className="text-gray-700 text-center">{profile.bio}</p>
        )}

        {/* How to Book */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4" />
            How to Book
          </h3>
          <p className="text-amber-800 text-sm">
            {profile.booking_instructions || "DM me on Instagram to book your appointment!"}
          </p>
          
          {profile.instagram && (
            <a
              href={`https://instagram.com/${profile.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-medium"
            >
              <Instagram className="w-4 h-4" />
              DM on Instagram
            </a>
          )}
        </div>

        {/* Share Preferences CTA */}
        <div className="bg-gradient-to-br from-pink-500 to-purple-600 text-white rounded-xl p-5">
          <h3 className="font-bold text-lg mb-1">Already booked?</h3>
          <p className="text-white/80 text-sm mb-4">
            Share your preferences so {profile.business_name?.split(' ')[0] || 'your stylist'} can prepare for your appointment.
          </p>
          <Button 
            onClick={handleSharePreferences}
            className="w-full bg-white text-gray-900 hover:bg-gray-100"
            data-testid="share-preferences-btn"
          >
            <Share className="w-4 h-4 mr-2" />
            Share My Preferences
          </Button>
        </div>

        {/* Services */}
        {services.length > 0 && (
          <section>
            <h2 className="font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">Services and Pricing</h2>
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {services.map((service, idx) => (
                <div key={idx} className="p-4 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{service.name}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {service.duration} min
                      </span>
                      {service.deposit_required && (
                        <Badge variant="outline" className="text-xs">
                          ${service.deposit_amount} deposit
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-lg font-bold">${service.price}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Portfolio */}
        {portfolio.length > 0 && (
          <section>
            <h2 className="font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">Portfolio</h2>
            <div className="grid grid-cols-3 gap-2">
              {portfolio.map((item, idx) => (
                <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img 
                    src={item.image_url} 
                    alt={item.description || "Portfolio"} 
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Payment Options */}
        {profile.payment_options && Object.keys(profile.payment_options).filter(k => profile.payment_options[k]).length > 0 && (
          <section>
            <h2 className="font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">Payment Accepted</h2>
            <div className="flex flex-wrap gap-2">
              {profile.payment_options.venmo && (
                <Badge variant="outline" className="py-1.5">Venmo</Badge>
              )}
              {profile.payment_options.zelle && (
                <Badge variant="outline" className="py-1.5">Zelle</Badge>
              )}
              {profile.payment_options.cashapp && (
                <Badge variant="outline" className="py-1.5">Cash App</Badge>
              )}
              {profile.payment_options.apple_pay && (
                <Badge variant="outline" className="py-1.5">Apple Pay</Badge>
              )}
            </div>
          </section>
        )}

        {/* Footer */}
        <div className="text-center pt-4 pb-8">
          <div className="flex items-center justify-center gap-1 text-gray-500 text-xs">
            <PhygitalIcon className="w-4 h-4" showBox={false} />
            <span>Powered by phygital</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StylistPublicProfile;
